import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Card, Stack, Chip, Box,
  Divider
} from '@mui/material';
import { Preview as PreviewIcon } from '@mui/icons-material';

const th = {
  fontWeight: 600,
  backgroundColor: 'action.hover',
  whiteSpace: 'nowrap',
  fontSize: '0.85rem',
};

const labelCell = {
  fontWeight: 600,
  width: '28%',
  backgroundColor: 'action.hover',
  verticalAlign: 'top',
};

export default function WpPreviewDialog({ open, onClose, wp }) {
  if (!wp) return null;

  const deliverables = (wp.deliverables || []).map((d, i) => ({
    ...d,
    displayCode: `D${wp.number}.${i + 1}`,
  }));
  const milestones = (wp.milestones || []).map((m, i) => ({
    ...m,
    displayCode: `MS${wp.number}.${i + 1}`,
  }));
  const risks = (wp.risks || []).map((r, i) => ({
    ...r,
    displayCode: `R${wp.number}.${i + 1}`,
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper" sx={{ zIndex: 1600 }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PreviewIcon fontSize="small" color="primary" />
        WP{wp.number}: {wp.title}
      </DialogTitle>

      <DialogContent dividers>
        {/* WP description card */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TableCell sx={labelCell}>Work package number</TableCell>
                  <TableCell>{wp.number}</TableCell>
                </TableRow>

                <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TableCell sx={labelCell}>Work package title</TableCell>
                  <TableCell>{wp.title}</TableCell>
                </TableRow>

                {(wp.wp_leader_org_name || wp.wp_leader_org_short_name) && (
                  <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TableCell sx={labelCell}>WP leader</TableCell>
                    <TableCell>
                      {wp.wp_leader_org_name || '—'}
                      {wp.wp_leader_org_short_name ? ` (${wp.wp_leader_org_short_name})` : ''}
                    </TableCell>
                  </TableRow>
                )}

                <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TableCell sx={labelCell}>Duration</TableCell>
                  <TableCell>
                    M{wp.start_month ?? '—'} – M{wp.end_month ?? '—'}
                    {wp.total_person_months ? ` · ${wp.total_person_months} PM` : ''}
                  </TableCell>
                </TableRow>

                <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TableCell sx={labelCell}>Description of work</TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {wp.description && (
                        <Typography variant="body2">{wp.description}</Typography>
                      )}
                      {wp.tasks && wp.tasks.length > 0 ? (
                        <ul style={{ margin: '4px 0 0', paddingLeft: 20, listStyleType: 'none' }}>
                          {wp.tasks.map((task) => {
                            const mEnd = task.start_month && task.duration_months
                              ? task.start_month + task.duration_months - 1
                              : null;
                            const months = task.start_month
                              ? ` (M${task.start_month}${mEnd ? ` – M${mEnd}` : ''})`
                              : '';
                            return (
                              <li key={task.id}>
                                <Typography variant="body2">
                                  <strong>Task {wp.number}.{task.number} – {task.title}{months}:</strong>{' '}
                                  {task.description || <em style={{ opacity: 0.5 }}>No description</em>}
                                </Typography>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        !wp.description && <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCell}>Objectives</TableCell>
                  <TableCell>
                    {wp.objectives && wp.objectives.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: 20, listStyleType: 'none' }}>
                        {wp.objectives.map((obj, i) => (
                          <li key={obj.id || i}>
                            <Typography variant="body2">
                              <strong>O{wp.number}.{i + 1}</strong> – {obj.description}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Deliverables */}
        {deliverables.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Deliverables
            </Typography>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={th}>Code</TableCell>
                      <TableCell sx={th}>Title</TableCell>
                      <TableCell sx={th}>Description</TableCell>
                      <TableCell sx={th} align="center">Type</TableCell>
                      <TableCell sx={th} align="center">Dissemination</TableCell>
                      <TableCell sx={th} align="center">Month</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliverables.map((d, i) => (
                      <TableRow key={d.id || i} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{d.displayCode}</TableCell>
                        <TableCell>{d.title || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>{d.description || '—'}</TableCell>
                        <TableCell align="center">
                          {d.type ? <Chip label={d.type} size="small" variant="outlined" /> : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {d.dissemination_level ? <Chip label={d.dissemination_level} size="small" /> : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {d.delivery_month ? `M${d.delivery_month}` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        {/* Milestones */}
        {milestones.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Milestones
            </Typography>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={th}>Code</TableCell>
                      <TableCell sx={th}>Description</TableCell>
                      <TableCell sx={th}>Means of Verification</TableCell>
                      <TableCell sx={th} align="center">Due Month</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {milestones.map((m, i) => (
                      <TableRow key={m.id || i} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{m.displayCode}</TableCell>
                        <TableCell>{m.description || '—'}</TableCell>
                        <TableCell>{m.verification_means || '—'}</TableCell>
                        <TableCell align="center">
                          {m.due_month ? (
                            <Chip label={`M${m.due_month}`} size="small" color="warning" />
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        {/* Risks */}
        {risks.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Risks
            </Typography>
            <Card variant="outlined" sx={{ mb: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={th}>Code</TableCell>
                      <TableCell sx={th}>Description</TableCell>
                      <TableCell sx={th} align="center">Impact</TableCell>
                      <TableCell sx={th} align="center">Likelihood</TableCell>
                      <TableCell sx={th}>Mitigation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {risks.map((r, i) => (
                      <TableRow key={r.id || i} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{r.displayCode}</TableCell>
                        <TableCell>{r.description || '—'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={r.impact || '—'}
                            size="small"
                            color={r.impact === 'high' ? 'error' : r.impact === 'medium' ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={r.likelihood || '—'}
                            size="small"
                            variant="outlined"
                            color={r.likelihood === 'high' ? 'error' : r.likelihood === 'medium' ? 'warning' : 'success'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>{r.mitigation_plan || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        {deliverables.length === 0 && milestones.length === 0 && risks.length === 0 && (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No deliverables, milestones, or risks defined for this work package yet.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
